/**
 * Core domain types for the All-in-One Business AI platform
 * Browser-compatible version - Enhanced for SaaS-like experience
 *
 * References:
 * - Linear: Issue workflow states (triage/backlog/in_progress/in_review/done)
 * - ClickUp: Hierarchy & multi-view work management
 * - Notion: Docs/wiki organization, rich notes
 * - HubSpot: CRM pipeline stages, contact detail cards
 * - Stripe: Analytics cards, KPI dashboards
 */

export interface Entity {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Team & Roles (New - Inspired by Linear/ClickUp team features)
// ============================================================================

export type TeamRole = 'team_lead' | 'developer' | 'planner' | 'qa' | 'member';

export interface TeamMember extends Entity {
  workspaceId: string;
  name: string;
  email: string;
  avatar?: string;
  role: TeamRole;
  title?: string;
  department?: string;
  isActive: boolean;
}

export const ROLE_LABELS: Record<TeamRole, string> = {
  team_lead: 'Team Lead',
  developer: 'Developer',
  planner: 'Planner',
  qa: 'QA',
  member: 'Member',
};

export const ROLE_COLORS: Record<TeamRole, string> = {
  team_lead: 'purple',
  developer: 'blue',
  planner: 'orange',
  qa: 'green',
  member: 'gray',
};

// ============================================================================
// Workspace
// ============================================================================

export interface Workspace extends Entity {
  name: string;
  businessType: string;
  description: string;
  settings: WorkspaceSettings;
}

export interface WorkspaceSettings {
  timezone: string;
  currency: string;
  language: string;
  modules: ModuleConfig[];
}

export interface ModuleConfig {
  name: ModuleName;
  enabled: boolean;
  config: Record<string, unknown>;
}

export type ModuleName = 'notes' | 'tasks' | 'crm' | 'insights' | 'assistant' | 'team';

// ============================================================================
// Tasks - Linear-style workflow
// ============================================================================

// Linear-inspired workflow states
export type TaskStatus =
  | 'triage'      // New issues to be triaged
  | 'backlog'     // Accepted but not started
  | 'in_progress' // Currently being worked on
  | 'in_review'   // Awaiting review/approval
  | 'done'        // Completed
  | 'cancelled';  // Cancelled/won't do

export type TaskPriority = 'no_priority' | 'low' | 'medium' | 'high' | 'urgent';

export interface Task extends Entity {
  workspaceId: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: Date;
  assigneeId?: string;
  reporterId?: string;
  linkedEntities: LinkedEntity[];
  tags: string[];
  // Linear-style fields
  identifier?: string; // e.g., "TASK-123"
  estimate?: number;   // Story points or hours
  cycleId?: string;    // Sprint/Cycle reference
  projectId?: string;  // Project reference
}

export const TASK_STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; icon: string }> = {
  triage: { label: 'Triage', color: 'yellow', icon: '⚡' },
  backlog: { label: 'Backlog', color: 'gray', icon: '📋' },
  in_progress: { label: 'In Progress', color: 'blue', icon: '🔄' },
  in_review: { label: 'In Review', color: 'purple', icon: '👀' },
  done: { label: 'Done', color: 'green', icon: '✓' },
  cancelled: { label: 'Cancelled', color: 'slate', icon: '✕' },
};

export const TASK_PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string; level: number }> = {
  no_priority: { label: 'No Priority', color: 'gray', level: 0 },
  low: { label: 'Low', color: 'slate', level: 1 },
  medium: { label: 'Medium', color: 'blue', level: 2 },
  high: { label: 'High', color: 'orange', level: 3 },
  urgent: { label: 'Urgent', color: 'red', level: 4 },
};

// ============================================================================
// Notes - Notion-style organization
// ============================================================================

export interface Note extends Entity {
  workspaceId: string;
  title: string;
  content: string;
  tags: string[];
  linkedEntities: LinkedEntity[];
  // Notion-style fields
  icon?: string;       // Emoji or icon
  coverImage?: string; // Cover image URL
  parentId?: string;   // Parent note for hierarchy
  isArchived?: boolean;
  isPinned?: boolean;
}

export interface LinkedEntity {
  type: 'contact' | 'task' | 'note' | 'team_member';
  id: string;
}

// ============================================================================
// CRM - HubSpot-style pipeline
// ============================================================================

export type ContactStage =
  | 'lead'           // Initial contact
  | 'qualified'      // Qualified lead
  | 'proposal'       // Proposal sent
  | 'negotiation'    // In negotiation
  | 'customer'       // Converted customer
  | 'churned';       // Lost/churned

export interface Contact extends Entity {
  workspaceId: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  role?: string;
  stage: ContactStage;
  notes: string;
  tags: string[];
  customFields: Record<string, string>;
  // HubSpot-style fields
  ownerId?: string;         // Assigned team member
  dealValue?: number;       // Potential deal value
  lastContactedAt?: Date;   // Last interaction
  source?: string;          // Lead source
  avatar?: string;          // Contact avatar
}

export const CONTACT_STAGE_CONFIG: Record<ContactStage, { label: string; color: string; order: number }> = {
  lead: { label: 'Lead', color: 'slate', order: 0 },
  qualified: { label: 'Qualified', color: 'blue', order: 1 },
  proposal: { label: 'Proposal', color: 'indigo', order: 2 },
  negotiation: { label: 'Negotiation', color: 'purple', order: 3 },
  customer: { label: 'Customer', color: 'green', order: 4 },
  churned: { label: 'Churned', color: 'red', order: 5 },
};

// ============================================================================
// Insights - Stripe-style analytics
// ============================================================================

export interface Insight extends Entity {
  workspaceId: string;
  type: InsightType;
  title: string;
  summary: string;
  data: Record<string, unknown>;
  priority: 'info' | 'action' | 'urgent';
  // Stripe-style fields
  metric?: string;
  value?: number;
  change?: number;      // Percentage change
  trend?: 'up' | 'down' | 'stable';
}

export type InsightType = 'summary' | 'recommendation' | 'alert' | 'trend' | 'kpi';

// ============================================================================
// Assistant
// ============================================================================

export interface AssistantSuggestion extends Entity {
  workspaceId: string;
  type: SuggestionType;
  title: string;
  description: string;
  actionType?: string;
  actionPayload?: Record<string, unknown>;
  dismissed: boolean;
  priority?: 'low' | 'medium' | 'high';
}

export type SuggestionType = 'task' | 'follow_up' | 'reminder' | 'optimization' | 'insight';

// ============================================================================
// Onboarding & Setup
// ============================================================================

export interface OnboardingInput {
  businessDescription: string;
  goals?: string[];
  existingTools?: string[];
}

// ============================================================================
// ROI/Efficiency Diagnosis
// ============================================================================

export interface ROIDiagnosis {
  workspaceId: string;
  createdAt: Date;
  timeEfficiency: EfficiencyMetric;
  costEfficiency: EfficiencyMetric;
  automationPotential: EfficiencyMetric;
  overallScore: number; // 0-100
  estimatedMonthlySavings: number; // in currency
  estimatedTimeSavedHours: number; // per month
  recommendations: ROIRecommendation[];
}

export interface EfficiencyMetric {
  label: string;
  currentScore: number; // 0-100
  potentialScore: number; // 0-100 after optimization
  improvement: number; // percentage improvement
  description: string;
}

export interface ROIRecommendation {
  id: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  category: 'time' | 'cost' | 'automation' | 'growth';
  estimatedSavings?: number;
  estimatedTimeHours?: number;
}

export interface SetupResult {
  workspace: Workspace;
  generatedTasks: Task[];
  generatedContacts: Contact[];
  generatedNotes: Note[];
  suggestions: AssistantSuggestion[];
  insights: Insight[];
  teamMembers: TeamMember[];
}

export interface AIOrchestrationRequest {
  type: 'setup' | 'query' | 'action';
  input: OnboardingInput | string;
  context?: {
    workspaceId?: string;
    currentModule?: ModuleName;
  };
}

export interface AIOrchestrationResponse {
  success: boolean;
  result?: SetupResult | string | Record<string, unknown>;
  error?: string;
  suggestedActions?: AssistantSuggestion[];
}

// ============================================================================
// Dashboard KPIs - Stripe-inspired
// ============================================================================

export interface DashboardKPI {
  id: string;
  label: string;
  value: number;
  previousValue?: number;
  change?: number;
  trend?: 'up' | 'down' | 'stable';
  format: 'number' | 'currency' | 'percentage';
  color?: string;
}

export interface DashboardData {
  kpis: DashboardKPI[];
  taskSummary: {
    total: number;
    byStatus: Record<TaskStatus, number>;
    overdue: number;
    dueToday: number;
    completedThisWeek: number;
  };
  crmSummary: {
    total: number;
    byStage: Record<ContactStage, number>;
    totalDealValue: number;
    newLeadsThisWeek: number;
  };
  teamSummary: {
    total: number;
    byRole: Record<TeamRole, number>;
    active: number;
  };
  recentActivity: ActivityItem[];
}

export interface ActivityItem {
  id: string;
  type: 'task_created' | 'task_completed' | 'contact_added' | 'note_created' | 'deal_won' | 'member_joined';
  title: string;
  description?: string;
  timestamp: Date;
  actorId?: string;
  entityId?: string;
  entityType?: string;
}

// ============================================================================
// Build Review - Product improvement loop workflow
// ============================================================================

/**
 * Build Run represents a build/test/deploy attempt
 */
export type BuildRunStatus = 'running' | 'passed' | 'failed' | 'cancelled';

export interface BuildRun extends Entity {
  workspaceId: string;
  identifier: string;      // e.g., "BUILD-42"
  branch: string;
  commit: string;
  commitMessage: string;
  status: BuildRunStatus;
  triggeredBy: string;     // Team member ID
  duration?: number;       // in seconds
  testsPassed?: number;
  testsFailed?: number;
  testsSkipped?: number;
  logs?: string;
  artifacts?: string[];
}

export const BUILD_RUN_STATUS_CONFIG: Record<BuildRunStatus, { label: string; color: string; icon: string }> = {
  running: { label: 'Running', color: 'blue', icon: '🔄' },
  passed: { label: 'Passed', color: 'green', icon: '✓' },
  failed: { label: 'Failed', color: 'red', icon: '✕' },
  cancelled: { label: 'Cancelled', color: 'gray', icon: '⊘' },
};

/**
 * QA Item represents a failed test, caution, or improvement suggestion
 */
export type QASeverity = 'blocker' | 'warning' | 'suggestion';
export type QAStatus = 'open' | 'in_progress' | 'resolved' | 'wont_fix';

export interface QAItem extends Entity {
  workspaceId: string;
  buildRunId: string;
  identifier: string;      // e.g., "QA-123"
  title: string;
  description: string;
  severity: QASeverity;
  status: QAStatus;
  assigneeId?: string;
  category: string;        // e.g., "UI", "API", "Performance"
  screenshot?: string;
  stepsToReproduce?: string;
  linearIssueId?: string;  // Linked Linear issue
}

export const QA_SEVERITY_CONFIG: Record<QASeverity, { label: string; color: string; level: number }> = {
  blocker: { label: 'Blocker', color: 'red', level: 3 },
  warning: { label: 'Warning', color: 'orange', level: 2 },
  suggestion: { label: 'Suggestion', color: 'blue', level: 1 },
};

export const QA_STATUS_CONFIG: Record<QAStatus, { label: string; color: string }> = {
  open: { label: 'Open', color: 'yellow' },
  in_progress: { label: 'In Progress', color: 'blue' },
  resolved: { label: 'Resolved', color: 'green' },
  wont_fix: { label: "Won't Fix", color: 'gray' },
};

/**
 * Review Item represents a code/feature review in the approval flow
 */
export type ReviewStatus = 'draft' | 'review' | 'approved' | 'fix_required' | 'retest';

export interface ReviewItem extends Entity {
  workspaceId: string;
  identifier: string;      // e.g., "REV-15"
  title: string;
  description: string;
  status: ReviewStatus;
  authorId: string;        // Who created/submitted
  reviewerId?: string;     // Who is reviewing
  buildRunId?: string;     // Associated build
  qaItemIds: string[];     // Associated QA items
  linearIssueId?: string;  // Linked Linear issue
  linearState?: string;    // Last known Linear state
  linearSyncedAt?: Date;   // Last sync timestamp
  comments?: ReviewComment[];
  approvedAt?: Date;
  shippedAt?: Date;
}

export interface ReviewComment {
  id: string;
  authorId: string;
  content: string;
  createdAt: Date;
}

export const REVIEW_STATUS_CONFIG: Record<ReviewStatus, { label: string; color: string; icon: string }> = {
  draft: { label: 'Draft', color: 'gray', icon: '📝' },
  review: { label: 'In Review', color: 'purple', icon: '👀' },
  approved: { label: 'Approved', color: 'green', icon: '✓' },
  fix_required: { label: 'Fix Required', color: 'orange', icon: '⚠️' },
  retest: { label: 'Retest', color: 'blue', icon: '🔄' },
};

// ============================================================================
// Landing Page Canvas - Website builder concepts
// ============================================================================

export interface LandingPage extends Entity {
  workspaceId: string;
  title: string;
  heroSubtitle: string;
  heroCtaText: string;
  heroCtaUrl: string;
  primaryColor: string;
  accentColor: string;
  features: LandingPageFeature[];
  sections: LandingPageSection[];
  isPublished: boolean;
  publishedUrl?: string;
}

export interface LandingPageFeature {
  id: string;
  icon: string;
  title: string;
  description: string;
}

export interface LandingPageSection {
  id: string;
  type: 'features' | 'testimonials' | 'pricing' | 'contact' | 'about';
  title: string;
  content: string;
  order: number;
}
